import { signInWithEmailAndPassword } from "firebase/auth";
import { router } from "expo-router";
import React, { useState } from "react";
import { ActivityIndicator, Platform, Pressable, ScrollView, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Text, TextInput as BrandTextInput } from "@/src/components/ui/BrandText";
import { useAuth } from "@/src/hooks/useAuth";
import { accountDeletionErrorMessage, deleteCurrentAccount } from "@/src/lib/accountDeletion";
import { auth } from "@/src/lib/firebase";
import { border, brand, surface, text as uiText } from "@/src/theme/tokens";
import { contentMaxWidths } from "@/src/theme/layout";

export default function DeleteAccountScreen() {
  const { user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function onSignIn() {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      setPassword("");
    } catch {
      setError("Connexion impossible. Vérifie ton adresse email et ton mot de passe.");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await deleteCurrentAccount(password);
      router.replace("/(auth)/login");
    } catch (cause: any) {
      setError(accountDeletionErrorMessage(
        cause,
        "La suppression du compte a échoué. Vérifie ton mot de passe et réessaie."
      ));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: surface.secondary }}>
      <ScrollView
        contentContainerStyle={{
          alignSelf: "center",
          maxWidth: contentMaxWidths.settings,
          width: "100%",
          paddingHorizontal: 20,
          paddingVertical: 28,
          gap: 16,
        }}
        showsVerticalScrollIndicator={Platform.OS === "web"}
      >
        <View style={{ gap: 8 }}>
          <Text style={{ color: uiText.primary, fontSize: 30, fontWeight: "900" }}>Suppression du compte Neqraw</Text>
          <Text style={{ color: uiText.secondary, lineHeight: 22 }}>
            Cette page est accessible publiquement. Connecte-toi pour demander la suppression de ton compte, puis confirme ton mot de passe.
          </Text>
        </View>

        <View
          style={{
            backgroundColor: surface.primary,
            borderRadius: 16,
            borderWidth: 1,
            borderColor: border.subtle,
            padding: 18,
            gap: 12,
          }}
        >
          <Text style={{ color: uiText.primary, fontSize: 18, fontWeight: "900" }}>Ce qui est supprimé</Text>
          <Text style={{ color: uiText.secondary, lineHeight: 22 }}>
            Neqraw supprime le compte Firebase, l’identité Personnelle, son profil public, sa réservation de nom d’utilisateur, ses publications et commentaires personnels, ses relations sociales, ses demandes en attente, ses jetons push et sa photo de profil.
          </Text>
          <Text style={{ color: uiText.secondary, lineHeight: 22 }}>
            Les messages déjà remis aux autres participants peuvent rester dans leur historique pour préserver leurs conversations. Leur contenu est conservé, mais le nom et la photo de l’expéditeur sont remplacés par « Compte supprimé ». Les contenus partagés copiés dans une conversation peuvent également y rester. Les données de signalement, de modération et de sécurité peuvent être conservées lorsqu’elles sont nécessaires à l’intégrité du service.
          </Text>
          <Text style={{ color: uiText.secondary, lineHeight: 22 }}>
            Une identité Institution ou un groupe dont tu es le seul responsable n’est pas supprimé automatiquement. Dans ce cas, la demande est bloquée avant toute suppression. L’application ne propose pas encore de transfert pour une Institution ou un groupe dont tu es l’unique responsable. Une Institution ayant un autre propriétaire actif est conservée et ton accès personnel est retiré.
          </Text>
        </View>

        <View
          style={{
            backgroundColor: surface.primary,
            borderRadius: 16,
            borderWidth: 1,
            borderColor: border.subtle,
            padding: 18,
            gap: 12,
          }}
        >
          {loading ? (
            <ActivityIndicator color={brand.primary} />
          ) : user ? (
            <>
              <Text style={{ color: uiText.primary, fontSize: 18, fontWeight: "900" }}>
                {"Connecté" + (user.email ? " : " + user.email : "")}
              </Text>
              <Text style={{ color: uiText.secondary, lineHeight: 21 }}>
                Confirme ton mot de passe récent pour supprimer définitivement le compte connecté.
              </Text>
              <BrandTextInput
                value={password}
                onChangeText={setPassword}
                placeholder="Mot de passe actuel"
                placeholderTextColor={uiText.muted}
                secureTextEntry
                editable={!busy}
                accessibilityLabel="Mot de passe actuel"
                style={inputStyle}
              />
              {error ? <Text accessibilityRole="alert" style={{ color: uiText.danger, lineHeight: 20 }}>{error}</Text> : null}
              {confirming ? (
                <>
                  <Text style={{ color: uiText.danger, fontWeight: "800", lineHeight: 21 }}>
                    Cette suppression est définitive et ne peut pas être annulée.
                  </Text>
                  <View style={{ flexDirection: "row", gap: 10 }}>
                    <ActionButton
                      label="Annuler"
                      disabled={busy}
                      onPress={() => setConfirming(false)}
                    />
                    <ActionButton
                      label="Supprimer définitivement"
                      disabled={busy}
                      danger
                      onPress={() => void onDelete()}
                    />
                  </View>
                </>
              ) : (
                <ActionButton
                  label="Continuer vers la suppression"
                  disabled={busy}
                  danger
                  onPress={() => {
                    setError("");
                    setConfirming(true);
                  }}
                />
              )}
            </>
          ) : (
            <>
              <Text style={{ color: uiText.primary, fontSize: 18, fontWeight: "900" }}>Connecte-toi pour continuer</Text>
              <BrandTextInput
                value={email}
                onChangeText={setEmail}
                placeholder="Adresse email"
                placeholderTextColor={uiText.muted}
                autoCapitalize="none"
                keyboardType="email-address"
                editable={!busy}
                accessibilityLabel="Adresse email du compte Neqraw"
                style={inputStyle}
              />
              <BrandTextInput
                value={password}
                onChangeText={setPassword}
                placeholder="Mot de passe"
                placeholderTextColor={uiText.muted}
                secureTextEntry
                editable={!busy}
                accessibilityLabel="Mot de passe du compte Neqraw"
                style={inputStyle}
              />
              {error ? <Text accessibilityRole="alert" style={{ color: uiText.danger, lineHeight: 20 }}>{error}</Text> : null}
              <ActionButton label="Se connecter" disabled={busy} onPress={() => void onSignIn()} />
            </>
          )}
          {busy ? <ActivityIndicator color={brand.primary} /> : null}
        </View>

        <Pressable
          accessibilityRole="link"
          onPress={() => router.push("/privacy")}
          style={({ pressed }) => ({ alignSelf: "center", opacity: pressed ? 0.72 : 1, padding: 8 })}
        >
          <Text style={{ color: brand.primary, fontWeight: "800" }}>Lire la politique de confidentialité</Text>
        </Pressable>
      </ScrollView>
    </SafeAreaView>
  );
}

const inputStyle = {
  minHeight: 48,
  paddingHorizontal: 13,
  borderRadius: 12,
  borderWidth: 1,
  borderColor: border.subtle,
  backgroundColor: surface.secondary,
  color: uiText.primary,
};

function ActionButton({
  label,
  onPress,
  disabled,
  danger = false,
}: {
  label: string;
  onPress: () => void;
  disabled: boolean;
  danger?: boolean;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      disabled={disabled}
      onPress={onPress}
      style={({ pressed }) => ({
        flex: 1,
        minHeight: 48,
        paddingHorizontal: 12,
        justifyContent: "center",
        alignItems: "center",
        borderRadius: 12,
        borderWidth: 1,
        borderColor: danger ? border.danger : border.subtle,
        backgroundColor: danger ? surface.danger : surface.secondary,
        opacity: disabled ? 0.5 : pressed ? 0.72 : 1,
      })}
    >
      <Text style={{ textAlign: "center", color: danger ? uiText.danger : uiText.primary, fontWeight: "900" }}>
        {label}
      </Text>
    </Pressable>
  );
}
